import {useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {UploadCloud, CheckCircle2, AlertCircle, Info} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Input,
  Progress,
  ProgressLabel,
  ProgressValue,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {scrapyCollectionsApi} from '@/api/scrapyCollections';
import {queryKeys} from '@/api/hooks';
import {parseCollectionsCsv, type CsvImportRow} from '@/lib/collectionCsv';
import {getApiErrorMessage} from '@/api/client';

// Bounds the upload so a pathological file can't be read into memory unbounded.
const MAX_FILE_SIZE = 5 * 1024 * 1024;

type Phase = 'select' | 'preview' | 'importing' | 'done';

export function ImportScrapyCollectionsDialog({onClose}: {readonly onClose: () => void}) {
  const qc = useQueryClient();

  const [phase, setPhase] = useState<Phase>('select');
  const [fileName, setFileName] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [rows, setRows] = useState<CsvImportRow[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);

  const [progress, setProgress] = useState(0);
  const [created, setCreated] = useState(0);
  const [updated, setUpdated] = useState(0);
  const [failures, setFailures] = useState<string[]>([]);

  async function handleFileSelected(file: File) {
    setParseError(null);
    setFileName(file.name);

    if (file.size > MAX_FILE_SIZE) {
      setParseError('Could not read the file. It may exceed the 5 MB limit.');
      return;
    }

    try {
      const content = await file.text();
      const result = parseCollectionsCsv(content);
      setRows(result.rows);
      setWarnings(result.warnings);
      setPhase('preview');
    } catch {
      setParseError('Could not parse the file as CSV.');
    }
  }

  async function handleImport() {
    setPhase('importing');
    setProgress(0);
    setCreated(0);
    setUpdated(0);
    setFailures([]);

    // A row updates an existing collection in place when its name matches one that already
    // exists (or one imported earlier in this run); every other row is created.
    let existingByName: Map<string, string>;
    try {
      const existing = await scrapyCollectionsApi.list();
      existingByName = new Map(existing.map((c) => [c.name.trim(), c.id]));
    } catch {
      setFailures(['Could not load existing collections to match names; import aborted.']);
      setPhase('done');
      return;
    }

    let createdCount = 0;
    let updatedCount = 0;
    const failureMessages: string[] = [];

    for (const row of rows) {
      const name = row.name.trim();
      try {
        const existingId = existingByName.get(name);
        if (existingId) {
          await scrapyCollectionsApi.update(existingId, {
            name,
            description: row.description,
            spider_args: row.spiderArgs,
            cron_expression: row.cronExpression,
            ...(row.cronExpression ? {} : {clear_cron_expression: true}),
            affiliate: row.affiliate,
            policy_violation: row.policyViolation,
            language: row.language
          });
          updatedCount++;
        } else {
          // New collections are imported switched off — enabling (and thus scheduling) is a
          // deliberate opt-in step after review, never an automatic side effect of import.
          const createdCollection = await scrapyCollectionsApi.create({
            name,
            description: row.description,
            spider_name: row.spiderName,
            spider_args: row.spiderArgs,
            cron_expression: row.cronExpression,
            is_active: false,
            affiliate: row.affiliate,
            policy_violation: row.policyViolation,
            language: row.language
          });
          createdCount++;
          existingByName.set(name, createdCollection.id);
        }
      } catch (error) {
        const reason = getApiErrorMessage(error, 'an unexpected error occurred');
        failureMessages.push(`Row ${row.rowNumber} (${name}): ${reason}`);
      } finally {
        setProgress((p) => p + 1);
        setCreated(createdCount);
        setUpdated(updatedCount);
        setFailures([...failureMessages]);
      }
    }

    await qc.invalidateQueries({queryKey: queryKeys.scrapyCollections});
    setPhase('done');
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && phase !== 'importing') {
          onClose();
        }
      }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Import Collections from CSV</DialogTitle>
          <DialogDescription>
            Rows whose name matches an existing collection update it in place; everything else is
            created switched off until reviewed and enabled.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {phase === 'select' ? (
            <>
              <p className="text-muted-foreground text-xs leading-relaxed">
                Upload a CSV of collection configurations. Columns, in order:{' '}
                <strong className="text-foreground font-medium">
                  Name, Description, Query, SpiderName, Schedule, DaysLimit, Affiliate, Policy
                  Violation, Language
                </strong>
                , then one <code className="font-mono">key=value</code> spider argument per
                remaining column. Affiliate, Policy Violation and Language must match an existing
                customer/policy violation/language (case-insensitive) or the row will fail. Rows
                whose name matches an existing collection update it in place, keeping its current
                enabled/disabled state; everything else is created switched off until reviewed and
                enabled.
              </p>

              {/* A real file input rather than a hidden one behind a button, so the drop zone is
                  reachable and operable from the keyboard without a click-forwarding ref. */}
              <Empty variant="outline">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <UploadCloud />
                  </EmptyMedia>
                  <EmptyTitle>Choose CSV file</EmptyTitle>
                  <EmptyDescription>Up to 5 MB.</EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <Input
                    type="file"
                    accept=".csv"
                    aria-label="Choose CSV file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        void handleFileSelected(file);
                      }
                    }}
                  />
                </EmptyContent>
              </Empty>

              {parseError ? (
                <Alert variant="destructive">
                  <AlertCircle />
                  <AlertDescription>{parseError}</AlertDescription>
                </Alert>
              ) : null}
            </>
          ) : null}

          {phase === 'preview' || phase === 'importing' ? (
            <>
              <div>
                <p className="text-foreground text-xs font-medium">{fileName}</p>
                <p className="text-muted-foreground text-xs">
                  {rows.length} configuration{rows.length === 1 ? '' : 's'} ready to import.
                </p>
              </div>

              {warnings.length > 0 ? (
                <Alert>
                  <Info />
                  <AlertTitle>
                    {warnings.length} note{warnings.length === 1 ? '' : 's'} from parsing:
                  </AlertTitle>
                  <AlertDescription>
                    <ul className="list-inside list-disc">
                      {warnings.map((w) => (
                        <li key={w}>{w}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              ) : null}

              {phase === 'importing' ? (
                <Progress value={progress} max={Math.max(rows.length, 1)}>
                  <ProgressLabel>Processing…</ProgressLabel>
                  <ProgressValue>{() => `${progress} / ${rows.length}`}</ProgressValue>
                </Progress>
              ) : null}

              {rows.length === 0 ? (
                <p className="text-muted-foreground text-xs">No valid rows to import.</p>
              ) : null}
            </>
          ) : null}

          {phase === 'done' ? (
            <>
              <p className="text-foreground text-xs font-medium">Import complete</p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">
                  <CheckCircle2 /> {created} created
                </Badge>
                <Badge>
                  <CheckCircle2 /> {updated} updated
                </Badge>
                {failures.length > 0 ? (
                  <Badge variant="destructive">
                    <AlertCircle /> {failures.length} failed
                  </Badge>
                ) : null}
              </div>

              {failures.length > 0 ? (
                <Alert variant="destructive">
                  <AlertCircle />
                  <AlertTitle>Rows that failed</AlertTitle>
                  <AlertDescription>
                    <ul className="list-inside list-disc">
                      {failures.map((f) => (
                        <li key={f}>{f}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              ) : null}
            </>
          ) : null}
        </div>

        <DialogFooter>
          {phase === 'done' ? (
            <Button type="button" onClick={onClose}>
              Close
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                disabled={phase === 'importing'}
                onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="button"
                disabled={phase !== 'preview' || rows.length === 0}
                onClick={() => void handleImport()}>
                {phase === 'importing' ? <Spinner /> : null}
                Import {rows.length}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
